import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, message, Calendar } from 'antd';
import commonS from 'styles/common.module.less';
import s from './s.module.less';
import cls from 'classnames';

import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { TimeItemType, IproviderOverrideCreate } from 'types/appointmentSetting';
import { providerOverrideCreate, providerOverrideDelete } from 'api/calendarSetting';
import { getUserId, getTimeZone } from 'utils/localstore';
import { getDateKey } from 'utils/calendar';
import { OverrideFormInterface } from './types';
import TimeSelect from 'components/form/TimeSelect';
import greenPlusIcon from 'assets/common/greenPlus.svg';
import trashIcon from 'assets/common/trash.svg';
import arrowRightIcon from 'assets/common/arrowRightIcon.svg';
import doubleRightIcon from 'assets/common/doubleRIght.svg';
import type { IProviderOverrideListWithDates } from './types';

type Props = {
    onCancel?: () => void,
    onFetchData?: () => void
    editItem: IProviderOverrideListWithDates | null,
};

const initValues: TimeItemType = {
    fromHour: undefined,
    fromMinute: undefined,
    toHour: undefined,
    toMinute: undefined,
};

export default function AddOverrideModal({ onCancel, onFetchData, editItem }: Props) {
    const [form] = Form.useForm<OverrideFormInterface>();
    const [submiting, setSubmiting] = useState(false);
    const [overrideTime, setOverrideTime] = useState<TimeItemType[]>([{ ...initValues }]);
    const uid = getUserId();
    const [selectedDates, setSelectedDates] = useState<Dayjs[]>([dayjs()]);

    useEffect(() => {
        if (editItem) {
            if (editItem.time && editItem.time.length > 0) {
                const timeData = editItem.time.map((item) => ({
                    fromHour: item.fromHour,
                    fromMinute: item.fromMinute,
                    toHour: item.toHour,
                    toMinute: item.toMinute,
                }));
                setOverrideTime(timeData);
            } else {
                setOverrideTime([{ ...initValues }]);
            }

            if (editItem.overrideDates) {
                const dateObj = editItem.overrideDates.map((item) => dayjs(item.date, 'YYYY-MM-DD'));
                setSelectedDates(dateObj);
            }
        }
    }, [editItem]);

    const toggleDate = (current: Dayjs) => {
        const exists = selectedDates.some((date) => date.isSame(current, 'day'));
        if (exists) {
            setSelectedDates(selectedDates.filter((date) => !date.isSame(current, 'day')));
        } else {
            setSelectedDates([...selectedDates, current]);
        }
    };

    const addBlockTime = () => {
        if (overrideTime.length === 5) return message.error('Max 5 time slot per day');
        const newTimeList = [...overrideTime, { ...initValues }];
        setOverrideTime(newTimeList);
    };

    const timePickerFunc = (val: TimeItemType, index: number) => {
        if (!val) return;

        const timeItem = {
            fromHour: val.fromHour !== undefined ? val.fromHour : initValues.fromHour,
            fromMinute: val.fromMinute !== undefined ? val.fromMinute : initValues.fromMinute,
            toHour: val.toHour !== undefined ? val.toHour : initValues.toHour,
            toMinute: val.toMinute !== undefined ? val.toMinute : initValues.toMinute,
        };

        const newTimeList = [...overrideTime];
        newTimeList[index] = timeItem;
        setOverrideTime(newTimeList);
    };

    const deleteDateItem = (index: number) => {
        if (overrideTime.length <= 1) {
            setOverrideTime([{ ...initValues }]);
            return;
        }

        const newTimeList = overrideTime.filter((_, i) => i !== index);
        setOverrideTime(newTimeList);
    };

    const onCreate = async () => {
        // 验证 overrideTime 是否填写完整
        const hasIncompleteTime = overrideTime.some((time) =>
            time.fromHour === undefined ||
            time.fromMinute === undefined ||
            time.toHour === undefined ||
            time.toMinute === undefined,
        );

        if (hasIncompleteTime) {
            message.error('Please fill in all time slots completely');
            return false;
        }

        const params: IproviderOverrideCreate[] = selectedDates.map((date) => ({
            overrideDate: getDateKey(date),
            time: overrideTime,
            providerId: uid,
            timezone: getTimeZone() || '',
        }));
        if (editItem) {
            params.forEach((item, index) => {
                item.id = editItem.overrideDates.find(({ date }) => date === selectedDates[index].format('YYYY-MM-DD'))?.id;
            });
        }

        // 如果在编辑时有日期被删除，需要单独删除对应的 override
        const deletedIds = editItem?.overrideDates.filter(({ date }) => !selectedDates.some((item) => item.format('YYYY-MM-DD') === date));
        if (deletedIds && deletedIds.length > 0) {
            await providerOverrideDelete(deletedIds.map(({ id }) => id));
        }

        const { data } = await providerOverrideCreate(params);
        if (data.code) {
            message.success(data.message);
            onCancel?.();
            onFetchData?.();
            return true;
        }
        return false;
    };

    const onSave = async () => {
        setSubmiting(true);
        try {
            await form.validateFields();

            // 验证是否至少选择了一个日期
            if (selectedDates.length === 0) {
                message.error('Please select at least one date');
                setSubmiting(false);
                return;
            }

            // 验证 overrideTime 是否填写完整
            const hasIncompleteTime = overrideTime.some((time) =>
                time.fromHour === undefined ||
                time.fromMinute === undefined ||
                time.toHour === undefined ||
                time.toMinute === undefined,
            );

            if (hasIncompleteTime) {
                message.error('Please fill in all time slots completely');
                setSubmiting(false);
                return;
            }

            let success = false;

            const result = await onCreate();
            success = result;

            if (!success) {
                message.error('An error occurred during save');
            }
        } catch (error) {
            console.error(error);
        } finally {
            setSubmiting(false);
        }
    };

    const dateFullCellRender = (current: Dayjs) => {
        const isSelected = selectedDates.some((date) => date.isSame(current, 'day'));

        return (
            <div
                style={{
                    background: isSelected ? '#00816B' : 'transparent',
                    color: isSelected ? 'white' : 'inherit',
                    borderColor: isSelected ? '#00816B' : 'transparent',
                }}
                className={s.dateCell}
                onClick={(e) => {
                    e.stopPropagation();
                    toggleDate(current);
                }}
            >
                {current.date()}
            </div>
        );
    };

    return (
        <Modal
            title="Select the date(s) you want to assign specific available hours"
            closable
            className={cls(commonS.modalFixHeightWrap, s.addOverrideModal)}
            width="400px"
            destroyOnClose
            onCancel={onCancel}
            okText="Save"
            open
            footer={
                <>
                    <Button onClick={onCancel}>Cancel</Button>
                    <Button loading={submiting} type="primary" onClick={onSave}>Save</Button>
                </>
            }
        >
            <div className={s.wrap}>
                <Calendar
                    disabledDate={(date) => {
                        const now = new Date();
                        now.setHours(0, 0, 0, 0);
                        const todayStart = now.getTime();

                        if (!date) {
                            return false;
                        }
                        // 只禁用严格小于今天的日期，允许选择今天及以后的日期
                        return date.valueOf() < todayStart;
                    }}
                    fullscreen={false}
                    headerRender={({ value: headerValue, onChange }) => (
                        <div className="flex justify-between items-center">
                            <div>
                                <Button type="text" onClick={() => onChange(headerValue.subtract(1, 'year'))}>
                                    <img className="rotate-180" src={doubleRightIcon} />
                                </Button>
                                <Button type="text" onClick={() => onChange(headerValue.subtract(1, 'month'))}>
                                    <img className="scale-110 rotate-180" src={arrowRightIcon} />
                                </Button>
                            </div>
                            <span style={{ fontSize: 16, fontWeight: 'bold' }}>
                                {headerValue.format('MMM YYYY')}
                            </span>
                            <div>
                                <Button type="text" onClick={() => onChange(headerValue.add(1, 'month'))}>
                                    <img className="scale-110" src={arrowRightIcon} />
                                </Button>
                                <Button type="text" onClick={() => onChange(headerValue.add(1, 'year'))}>
                                    <img src={doubleRightIcon} />
                                </Button>
                            </div>
                        </div>
                    )}
                    dateFullCellRender={dateFullCellRender}
                />
                <Form
                    form={form}
                    name="basic"
                    className={commonS.formStyle1}
                    initialValues={{ remember: true }}
                    autoComplete="off"
                    layout="vertical"
                >
                    <div className={s.availableHours}>
                        <div className={s.title}>Available hours</div>
                        <div className="flex justify-between items-start m-2">
                            <div className="flex flex-col">
                                {overrideTime.map((dateItem, index) => {
                                    const otherTimes = overrideTime.filter((_, i) => i !== index);
                                    return (
                                        <div key={index} className="mt-1 mb-1 flex items-center">
                                            <TimeSelect
                                                value={dateItem}
                                                onChange={(val) => timePickerFunc(val || { ...initValues }, index)}
                                                disabledTimes={otherTimes}
                                            />
                                            <img
                                                className="ml-2 w-6 h-6 text-gray-400 cursor-pointer"
                                                onClick={() => deleteDateItem(index)}
                                                src={trashIcon}
                                                alt="delete"
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                            <img
                                className="w-6 h-6 cursor-pointer mt-2"
                                onClick={addBlockTime}
                                src={greenPlusIcon}
                                alt="add"
                            />
                        </div>
                    </div>
                </Form>
            </div>
        </Modal>
    );
}
